// Constants
import { AssociationType } from "@ms/uno-constants/lib/local/AppConstants";
import { MaxPlanContextLinks } from "@ms/uno-constants/lib/local/TeamsConstants";
// Utilities
import { isSharepointAppId, isTeamsPlannerClientEntraAppId } from "@ms/uno-utilities/lib/local/AppUtilities";
import { compareTemporalDate } from "@ms/uno-utilities/lib/local/Comparators";
import { getBreadcrumbString } from "@ms/uno-utilities/lib/local/StringUtilities";
/**
 * Takes a plan and returns the sorted plan context links that are displayable to the user.
 * This should be used for getting the context for breadcrumbs and for delete plan dialog.
 * @param plan The plan
 */ export function getSortedDisplayableContextLinks(plan) {
    const contextsToDisplay = plan.contexts.filter((contextLink)=>contextLink.isVisible());
    contextsToDisplay.sort((a, b)=>compareTemporalDate(a.createdDateTime, b.createdDateTime));
    return contextsToDisplay;
}
/**
 * Takes the display names array from plan context and returns a display string for the context.
 * This is used to display the context in the UI.
 * @param planContext - The plan context link to get the display text for.
 * @param containerDisplayName - The current display name of the container (e.g. group name) that the plan is in.
 */ export function getSyncContextDisplayText(planContext, containerDisplayName) {
    return getBreadcrumbString(getLatestDisplayNames(planContext, containerDisplayName));
}
/**
 * Returns refreshed display names with refreshed values if current containerDisplayName is specified.
 * This will replace proper container display name with the given latest one. For instance, Teams context might have stale group name on stored values for [ <group name>, <channel name> ] .
 * If no containerDisplayName is specified it just returns displayNames.
 * @param planContext - The plan context to get the display text for.
 * @param containerDisplayName - The current display name of the container (e.g. group name) that the plan is in.
 */ function getLatestDisplayNames(planContext, containerDisplayName) {
    // If the plan is associated with a Teams/SPO, we want to show the current group name since displayNames[0] can have a stale group name not updatable on this client
    const { displayNameSegments, ownerAppId, associationType } = planContext;
    if (!!containerDisplayName && ownerAppId != null) {
        if (isTeamsPlannerClientEntraAppId(ownerAppId) && associationType === AssociationType.TeamsAppBoard && displayNameSegments.length === 2 && displayNameSegments[0] !== containerDisplayName) {
            return [
                containerDisplayName,
                displayNameSegments[1]
            ];
        } else if (isSharepointAppId(ownerAppId) && associationType === AssociationType.SharePointSPWeb && displayNameSegments.length === 1 && displayNameSegments[0] !== containerDisplayName) {
            return [
                containerDisplayName
            ];
        }
    }
    return displayNameSegments;
}
/**
 * Enforces the maximum context link limit by removing the oldest context link if the limit is exceeded.
 * Mutates the provided array in place.
 * @param contextLinks The array of context links to enforce the limit on.
 */ export function enforceContextLinkLimit(contextLinks) {
    if (contextLinks.length > MaxPlanContextLinks) {
        const oldestIndex = contextLinks.reduce((minIndex, link, index)=>{
            return compareTemporalDate(link.createdDateTime, contextLinks[minIndex].createdDateTime) < 0 ? index : minIndex;
        }, 0);
        contextLinks.splice(oldestIndex, 1);
    }
}
